// File: components/DocumentUploadModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaUpload, FaFile } from 'react-icons/fa';
import gsap from 'gsap';

const DocumentUploadModal = ({ isOpen, onClose, documentType, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const modalRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const tl = gsap.timeline();
    tl.fromTo(modalRef.current,
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }
    ).fromTo(formRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.3 },
      "-=0.2"
    );

    return () => tl.kill();
  }, [isOpen]);

  const handleClose = () => {
    const tl = gsap.timeline();
    tl.to(formRef.current, { y: 30, opacity: 0, duration: 0.2 })
      .to(modalRef.current, { opacity: 0, scale: 0.8, duration: 0.3 }, "-=0.1")
      .eventCallback("onComplete", () => {
        setSelectedFile(null);
        onClose();
      });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        alert('Please select a PDF, image, or Word document');
        return;
      }
      
      setSelectedFile(file);
      console.log('📁 File selected:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    try {
      console.log('🚀 Starting upload process...');
      await onUpload(documentType, selectedFile);
      console.log('🎉 Upload process completed');
      handleClose();
    } catch (error) {
      console.error('💥 Upload process failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" ref={modalRef}>
      <div className="modal-content" ref={formRef}>
        <div className="modal-header">
          <h2>Upload {documentType}</h2>
          <button className="close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="upload-content">
          <div className="file-drop-zone">
            <FaFile className="file-icon" />
            <p>Select a file to upload</p>
            <input
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="file-input"
            />
            <button className="browse-btn">
              Browse Files
            </button>
          </div>
          
          {selectedFile && (
            <div className="file-preview">
              <h4>Selected File:</h4>
              <p><strong>Name:</strong> {selectedFile.name}</p>
              <p><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              <p><strong>Type:</strong> {selectedFile.type}</p>
            </div>
          )}
          
          <div className="upload-actions">
            <button 
              className="btn-secondary" 
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button 
              className="btn-primary"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              <FaUpload /> 
              {isUploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;